import { useState } from 'react';
import EPageHeader from '@/components/everridge/EPageHeader';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useTranslation } from 'react-i18next';
import { FilterHelper } from '@/utils/filterHelper';
import EFilterButton from '@/components/everridge/EFilterButton';
import SuperAdminDashboardPageFilter from '@/features/superAdmin/features/dashboard/components/SuperAdminDashboardPageFilter';
import { useSuperAdminDashboardFilter } from '@/features/superAdmin/features/dashboard/store/useSuperAdminDashboardFilter';
import ESearchInput from '@/components/everridge/ESearchInput';
import { useUserActivityListApi } from './hooks/useUserActivityListApi';
import useUserActivityListColumns from './hooks/useUserActivityListColumns';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import SuperAdminDashboardCharts from './components/SuperAdminDashboardCharts';

const SuperAdminDashboardPage = () => {
    const [showFilter, setShowFilter] = useState(false);

    const { userActivityListData, isLoadingUserActivityListData } = useUserActivityListApi();

    const { t } = useTranslation();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const userRole = useUserRole();

    const { filter, setFilter } = useSuperAdminDashboardFilter();
    const { page } = filter;

    const columns = useUserActivityListColumns();

    if (userRole !== Roles.superAdmin) return null;

    const filtersQuantity = FilterHelper.countFilterValues(filter, [
        'take',
        'skip',
        'orderBy',
        'orderDirection',
        'page',
        'searchText',
    ]);

    const handleSearchText = (text: string) => {
        setFilter({ page: 0, searchText: text });
    };

    const handlePageChange = (newPage: number) => {
        setFilter({
            skip: filter.take * newPage,
            page: newPage,
        });
    };

    return (
        <section className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader
                title={t('main.superAdmin.dashboard.title')}
                search={
                    <ESearchInput
                        value={filter.searchText}
                        setValue={handleSearchText}
                        placeholder={t('main.superAdmin.tenants.form.search.placeholder')}
                        containerClassName={cn(!isMobile && 'w-60')}
                    />
                }
            >
                <EFilterButton
                    name={t('elements.buttons.filter')}
                    filtersQuantity={filtersQuantity}
                    onClick={() => setShowFilter(true)}
                    isMobile={isMobile}
                />
            </EPageHeader>
            <div className={cn('h-full flex flex-col gap-6', isMobile && 'gap-4')}>
                <SuperAdminDashboardCharts />
                <ETableWithApiPagination
                    columns={columns}
                    data={userActivityListData}
                    paginationStep={filter.take}
                    page={page}
                    setPage={handlePageChange}
                    isLoadingPage={isLoadingUserActivityListData}
                />
            </div>
            {showFilter && (
                <SuperAdminDashboardPageFilter
                    onClose={() => setShowFilter(false)}
                    isMobile={isMobile}
                />
            )}
        </section>
    );
};

export default SuperAdminDashboardPage;
